import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { getContentIdFromPath, setActivationId } from '@databases/database-state';
import { DatabaseContentContext, setCloudId } from '@databases/database-state';
import { currentBridgeData } from '@databases/database-state/state/bridge-data';
import { updateFrameContext } from '@databases/database-state/state/host-frame-context';
import { featureFlagClientManager } from '@databases/feature-flags/manager';
import CommonApp from '@databases/frontend/App';
import AppContent from '@databases/frontend/AppContent';
import { perf } from '@databases/logs/performance';
import { appStore } from '@databases/redux/store';
import { getBridgeAPI } from '@databases/utils/database/bridge';
import { setContext } from '@databases/utils/redux/context/actions';
import { loadDatabaseByReferenceId } from '@databases/utils/redux/databases/actions';
import { withoutThrottledBatching } from '@databases/utils/redux/redux';
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useIntl } from 'react-intl-next';
import { useSnapshot } from 'valtio';

import { transformToken } from '@canvas/frame-api/canvas-frame-api';

import { changeTheme } from '../bootstrap/initialize-database-theme';

const App = () => {
  const {
    cloudId,
    spaceId,
    spaceKey,
    userId,
    contentId,
    activationId,
    contentReferenceId: referenceId,
    spaceName,
    title,
    token,
  } = useSnapshot(currentBridgeData).data;
  perf.timeLog('app-initialization', 'standalone-app-render');
  const intl = useIntl();
  const [databaseFlagsLoaded, setDatabaseFlagsLoaded] = useState(false);
  const previousContentId = useRef<string | null>(null);

  useEffect(() => {
    featureFlagClientManager.initialized().then(() => {
      setDatabaseFlagsLoaded(true);
    });

    // Focus iframe to enable keyboard shortcuts
    window.focus();

    const listenOnThemeChange = async () => {
      const bridge = await getBridgeAPI();
      if (bridge.onThemeChange) {
        bridge.onThemeChange((theme) => {
          changeTheme(theme);
        });
      }
    };

    listenOnThemeChange();
  }, []);

  useEffect(() => {
    updateFrameContext(transformToken(token));
  }, [token]);

  useEffect(() => {
    setCloudId(cloudId);
  }, [cloudId]);

  useEffect(() => {
    setActivationId(activationId);
  }, [activationId]);

  useEffect(() => {
    // eslint-disable-next-line require-await
    withoutThrottledBatching(async () => {
      appStore.dispatch(setContext({ space: { key: spaceKey }, userId }));
    });
  }, [spaceKey, userId]);

  useEffect(() => {
    // Ensure that Confluence side panel gets closed when SPA transitioning between databases
    const handleConfluenceSidePanelClose = async () => {
      const bridge = await getBridgeAPI();
      if (bridge?.onCloseConfluenceSidePanel) {
        return bridge.onCloseConfluenceSidePanel();
      }
    };

    if (!previousContentId.current) {
      previousContentId.current = contentId;
    } else if (previousContentId.current !== contentId) {
      handleConfluenceSidePanelClose();
      previousContentId.current = contentId;
    }
  }, [contentId]);

  const queryParams = useMemo(
    // eslint-disable-next-line no-restricted-syntax
    () => new URLSearchParams(window.parent.location.search),
    // eslint-disable-next-line no-restricted-syntax, react-hooks/exhaustive-deps
    [window.parent.location.search]
  );

  const getParentContentId = useCallback(() => {
    try {
      // eslint-disable-next-line no-restricted-syntax
      const pathname = window.top && window.top.location.pathname;
      return pathname ? getContentIdFromPath(pathname) : null;
    } catch {
      // can't access window.top if cross-origin
      return null;
    }
  }, []);

  const contentContext = useMemo(
    () => ({
      contentId,
      referencing: queryParams.get('mode') === 'referencing',
      activationId,
      cloudId,
      setContentId: () => {}, // used by referencing dialog, but not used in standalone (where confluence defines the contentId)
      spaceKey,
      savedViewId: queryParams.get('savedViewId'),
      unsavedViewId: queryParams.get('unsavedView'),
      entryId: queryParams.get('entryId'),
      fieldId: queryParams.get('fieldId'),
      referenceId,
      spaceId,
      parentContentId: getParentContentId(),
      spaceName,
      title,
    }),
    [
      contentId,
      activationId,
      cloudId,
      spaceKey,
      referenceId,
      spaceId,
      spaceName,
      title,
      queryParams,
      getParentContentId,
    ]
  );

  useEffect(() => {
    if (!contentId || !referenceId || !spaceId || !spaceKey) {
      return;
    }
    perf.timeLog('app-initialization', 'database-load-start');
    GlobalPageLoadExperience.mark('database-load-start');
    appStore.dispatch(
      loadDatabaseByReferenceId(
        {
          contentId,
          referenceId,
          spaceId: Number(spaceId),
          spaceKey,
          intl,
          spaceName: spaceName ?? '',
          title: title ?? '',
        },
        { throttle: false }
      )
    );
  }, [contentId, intl, referenceId, spaceId, spaceKey, spaceName, title]);

  if (!databaseFlagsLoaded) {
    return null;
  }

  return (
    <DatabaseContentContext.Provider value={contentContext}>
      <CommonApp aaid={userId}>
        <AppContent />
      </CommonApp>
    </DatabaseContentContext.Provider>
  );
};

export default React.memo(App);
